import { useEffect, useMemo, useRef } from "react";
import clsx from "clsx";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { RetentionPayDefault } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { useValidations } from "@/Common/hooks/Validations";
import { useCallbackState } from "@/Common/hooks/CallbackState";

// Custom components
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import LineInputsLoader from "@/Layouts/components/Skeletons/LineInputsLoader";

// MUI
import { Box, Grid, Stack, Typography } from "@mui/material";
import { and, renderIf } from "@/Common/helpers/sonar";

// Types
import { TAdvisorsRetentionPayContent, TAdvisorsRetentionPayProps } from "./types";
import { NumberOrString } from "@/Layouts/components/InputComponents/InputValue/types";

export default function AdvisorsRetentionPay(props: Readonly<TAdvisorsRetentionPayProps>) {
	const {
		sectionId = "retentionPay",
		content = {
			retention: {
				input: "Percent",
				value: 0,
			},
			internal: {
				input: "Percent",
				value: 0,
			},
			warranty: {
				input: "Percent",
				value: 0,
			},
		},
		loading = false,
		readOnly = false,
		useSectionsErrors,
		onActionUpdate,
	} = props;

	// Used to add errors to the pay plan context
	const { addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() ?? {};

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();

	const localError = errors?.get("retention");
	const errorsMessages = getErrorsMessages();

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onRetentionPayUpdate = (updatedRetentionPay: TAdvisorsRetentionPayContent) => {
		isValid ? removeSectionError?.(sectionId) : addSectionError?.(sectionId);
		if (and(isValid, wasUpdated.current)) {
			onActionUpdate?.(sectionId, null, updatedRetentionPay);
		}
	};

	// States
	const [retentionPay, setRetentionPay] = useCallbackState(RetentionPayDefault(), onRetentionPayUpdate);

	const update = (updatedRetentionPay: TAdvisorsRetentionPayContent) => {
		setRetentionPay(updatedRetentionPay);
		wasUpdated.current = true;
	};

	const handleUpdateInputType = (input: string) => {
		const updated = { ...retentionPay };
		updated["retention"] = { input, value: 0 };
		updated["internal"] = { input, value: 0 };
		updated["warranty"] = { input, value: 0 };
		update(updated);
	};

	const handleUpdateInputValue = (area: "retention" | "internal" | "warranty", value: NumberOrString) => {
		const updated = { ...retentionPay };
		updated[area].value = value ? Number(value) : 0;
		update(updated);
	};

	useMemo(() => {
		if (retentionPay) {
			validate([
				{
					definition: {
						useKey: "retention",
						operators: {
							conditions: [{ order: "ROWS", type: "SELF", on: "value", distinct: 0 }],
							evaluateAfterConditions: {
								mergeErrors: (_results: unknown, sections: [[TAdvisorsRetentionPayContent]]) => {
									const section = sections?.[0]?.[0];
									const hasError = and(!section?.retention?.value, !section?.internal?.value, !section?.warranty?.value);
									return [
										{
											retention: hasError,
											internal: hasError,
											warranty: hasError,
										},
									];
								},
							},
						},
						errorsMap: ["retention", "internal", "warranty"],
						errorsMapOn: "retention",
						message: "Invalid commission",
						validationType: "MATRIX",
					},
					section: [{ retention: [retentionPay] }],
				},
			]);
		}
	}, [retentionPay, validate]);

	useEffect(() => {
		setRetentionPay(clone(content));
		return () => {
			setRetentionPay(RetentionPayDefault());
			wasUpdated.current = false;
			clearSectionErrors?.(sectionId);
		};
	}, [sectionId, content, setRetentionPay, clearSectionErrors]);

	const isPercent = retentionPay?.["retention"]?.input === "Percent";

	return (
		<Grid container direction="column" className="px-8 py-0 mt-4">
			{loading ? (
				<Stack>
					<LineInputsLoader justifyContent="flex-start" mt={0} title inputsAmount={1} />
				</Stack>
			) : (
				<Stack direction="column" spacing={2}>
					<Stack direction="row" spacing={2}>
						<Stack
							className={clsx({
								"w-[100px]": !isPercent,
								"w-[95px]": isPercent,
							})}
						>
							{renderIf(
								isPercent,
								<Box className="text-center">
									<Typography className="!font-sans !text-default text-[14.5px] !mb-[6px]">Customer Pay</Typography>
								</Box>
							)}
							<InputFormExtension
								data={[
									{
										type: "Dollar",
										min: 0,
										max: 1000000,
										decimals: 2,
									},
									{
										type: "Percent",
										min: 0,
										max: 1000,
										decimalsEditable: true,
										decimals: 2,
									},
								]}
								defaultType={retentionPay?.["retention"]?.input || "Percent"}
								value={retentionPay?.["retention"]?.value || 0}
								error={localError?.some?.((error: { retention: string }) => error?.retention)}
								onActionUpdate={(value) => handleUpdateInputValue("retention", value)}
								onChangeType={(_, input) => handleUpdateInputType(input)}
								readOnly={readOnly}
							/>
						</Stack>
						<Stack width="95px" className={clsx({ hidden: !isPercent })}>
							<Box className="text-center">
								<Typography className="!font-sans !text-default text-[14.5px] !mb-[6px]">Internal Pay</Typography>
							</Box>
							<InputFormExtension
								data={[
									{
										type: "Dollar",
										min: 0,
										max: 1000000,
									},
									{
										type: "Percent",
										min: 0,
										max: 1000,
										decimalsEditable: true,
										decimals: 2,
									},
								]}
								defaultType={retentionPay?.["internal"]?.input || "Percent"}
								value={retentionPay?.["internal"]?.value || 0}
								error={localError?.some?.((error: { internal: string }) => error?.internal)}
								onActionUpdate={(value) => handleUpdateInputValue("internal", value)}
								onChangeType={(_, input) => handleUpdateInputType(input)}
								readOnly={readOnly}
							/>
						</Stack>
						<Stack width="95px" className={clsx({ hidden: !isPercent })}>
							<Box className="text-center">
								<Typography className="!font-sans !text-default text-[14.5px] !mb-[6px]">Warranty Pay</Typography>
							</Box>
							<InputFormExtension
								data={[
									{
										type: "Dollar",
										min: 0,
										max: 1000000,
									},
									{
										type: "Percent",
										min: 0,
										max: 1000,
										decimalsEditable: true,
										decimals: 2,
									},
								]}
								defaultType={retentionPay?.["warranty"]?.input || "Percent"}
								value={retentionPay?.["warranty"]?.value || 0}
								error={localError?.some?.((error: { warranty: string }) => error?.warranty)}
								onActionUpdate={(value) => handleUpdateInputValue("warranty", value)}
								onChangeType={(_, input) => handleUpdateInputType(input)}
								readOnly={readOnly}
							/>
						</Stack>
					</Stack>

					{Boolean(errorsMessages?.length) && (
						<Stack mt={1}>
							{errorsMessages.map((message, key) => {
								const compoundKey = "retention-pay-error-" + key;
								return <TierAlertError key={compoundKey} text={message} />;
							})}
						</Stack>
					)}
				</Stack>
			)}
		</Grid>
	);
}
